import React from 'react';
import { Box, Button, Paper, Typography, Alert, Chip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setUser } from '../store/slices/authSlice';

// Test data for admin functionality
const testUsers = {
  admin: {
    id: '1',
    email: 'admin@test.com',
    username: 'admin',
    full_name: 'System Administrator',
    is_active: true,
    is_superuser: true,
    roles: ['admin', 'superuser'],
    permissions: ['*'],
    subscription: {
      plan: 'enterprise',
      status: 'active',
      expires_at: '2024-12-31T23:59:59Z'
    },
    daily_posts_used: 5,
    daily_posts_limit: 100,
    created_at: '2024-01-01T00:00:00Z'
  },
  user: {
    id: '2',
    email: 'user@test.com',
    username: 'user',
    full_name: 'Regular User',
    is_active: true,
    is_superuser: false,
    roles: ['user'],
    permissions: ['posts.read', 'posts.write'],
    subscription: {
      plan: 'free',
      status: 'active'
    },
    daily_posts_used: 3,
    daily_posts_limit: 5,
    created_at: '2024-01-15T00:00:00Z'
  },
  manager: {
    id: '3',
    email: 'manager@test.com',
    username: 'manager',
    full_name: 'Content Manager',
    is_active: true,
    is_superuser: false,
    roles: ['user', 'manager'],
    permissions: ['posts.*', 'analytics.read'],
    subscription: {
      plan: 'pro',
      status: 'active',
      expires_at: '2024-06-30T23:59:59Z'
    },
    daily_posts_used: 15,
    daily_posts_limit: 50,
    created_at: '2024-01-10T00:00:00Z'
  }
};

export const AdminTestData: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);

  const switchUser = (userType: keyof typeof testUsers) => {
    dispatch(setUser(testUsers[userType]));
  };

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Typography variant="h6" gutterBottom>
        🧪 Development Test Data
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        This component is for development testing only. Use the buttons below to simulate different user roles.
      </Alert>

      <Typography variant="body2" gutterBottom>
        Current User: <strong>{user?.full_name || 'None'}</strong>
        {user?.roles && (
          <Box sx={{ mt: 1 }}>
            {user.roles.map(role => (
              <Chip key={role} label={role} size="small" sx={{ mr: 1 }} />
            ))}
          </Box>
        )}
      </Typography>

      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button 
          variant="contained" 
          color="error" 
          onClick={() => switchUser('admin')}
        >
          Switch to Admin
        </Button>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => switchUser('manager')}
        >
          Switch to Manager
        </Button>
        
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={() => switchUser('user')}
        >
          Switch to User
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Admin users can access the Admin Panel in the sidebar. Regular users will only see standard features.
      </Typography>
    </Paper>
  );
};

export default AdminTestData; 